import * as yup from "yup";
import { supportedEmailCharacters } from "../utils/emailSupportedCharacters";

// Common validation rules
const commonValidation = {
  email: yup
    .string()
    .email("Invalid email address")
    .test(supportedEmailCharacters)
    .required("Email address is required"),
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  isActive: yup.boolean().required("Please specify if active"),
  rights: yup
    .array()
    .transform((value, originalValue) => {
      // Always return all rights since they are pre-selected and disabled
      return [
        "Providers Released RFI",
        "Manage RFIs",
        "Manage Provider",
        "Manage Sponsor",
        "View Sponsor Activity",
        "View Provider Activity",
        "Manage User",
        "Standards",
        "Manage Provider RFIs",
        "Request Assessment",
        "Auditor Management",
        "Assessment Calendar",
        "Assessment Logs",
        "Receive Notifications",
      ];
    })
    .of(yup.string()),
  phoneNumber: yup
    .string()
    .nullable()
    .matches(
      /^[\d]{1}-[\d]{3}-[\d]{3}-[\d]{4}$/,
      "Phone number must be in the format 1-234-567-8900"
    )
    .required("Phone number is required"),
  officePhoneNumber: yup
    .string()
    .nullable()
    .matches(
      /^[\d]{1}-[\d]{3}-[\d]{3}-[\d]{4}$/,
      "Office phone number must be in the format 1-234-567-8900"
    )
    .required("Office phone number is required"),
  contactPreference: yup
    .string()
    .oneOf(["Office_Phone", "Mobile_Phone"], "Invalid contact preference")
    .required("Contact preference is required"),
};

// Common fields
const commonFields = [
  {
    label: "Email Address",
    name: "email",
    type: "text",
    placeholder: "Enter Email Address",
  },
  {
    label: "First Name",
    name: "firstName",
    type: "text",
    placeholder: "Enter First Name",
  },

  {
    label: "Last Name",
    name: "lastName",
    type: "text",
    placeholder: "Enter Last Name",
  },
  { label: "Active", name: "isActive", type: "radio", options: ["Yes", "No"] },
  {
    label: "Rights",
    name: "rights",
    type: "checkbox",
    disabled: true,
    options: [
      {
        value: "Providers Released RFI",
        label: "Providers Released VQQ",
        checked: true,
      },
      { value: "Manage RFIs", label: "Manage VQQs", checked: true },
      { value: "Manage Provider", label: "Manage Provider", checked: true },
      { value: "Manage Sponsor", label: "Manage Sponsor", checked: true },
      {
        value: "View Sponsor Activity",
        label: "View Sponsor Activity",
        checked: true,
      },
      {
        value: "View Provider Activity",
        label: "View Provider Activity",
        checked: true,
      },
      { value: "Manage User", label: "Manage User", checked: true },
      { value: "Standards", label: "Standards", checked: true },
      {
        value: "Manage Provider RFIs",
        label: "Manage Provider VQQs",
        checked: true,
      },
      {
        value: "Request Assessment",
        label: "Request Assessment",
        checked: true,
      },
      {
        value: "Auditor Management",
        label: "Auditor Management",
        checked: true,
      },
      {
        value: "Assessment Calendar",
        label: "Assessment Calendar",
        checked: true,
      },
      { value: "Assessment Logs", label: "Assessment Logs", checked: true },
      {
        value: "Receive Notifications",
        label: "Receive Notifications",
        checked: true,
      },
    ],
  },
  {
    name: "phoneNumber",
    label: "Phone Number",
    type: "tel",
    placeholder: "e.g. 1-234-567-8900",
    pattern: "^\\d{1}-\\d{3}-\\d{3}-\\d{4}$",
    format: "1-234-567-8900",
    title: "Phone number must be in the format X-XXX-XXX-XXXX",
  },
  {
    name: "officePhoneNumber",
    label: "Office Phone Number",
    type: "tel",
    placeholder: "e.g. 1-234-567-8900",
    pattern: "^\\d{1}-\\d{3}-\\d{3}-\\d{4}$",
    format: "1-234-567-8900",
    title: "Office phone number must be in the format X-XXX-XXX-XXXX",
  },
  {
    name: "contactPreference",
    label: "Contact Preference",
    type: "select",
    options: [
      { value: "Office_Phone", label: "Office Phone" },
      { value: "Mobile_Phone", label: "Mobile Phone" },
    ],
    required: true,
    className:
      "bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500",
  },
];

// Password pattern requirements:
// - At least 8 characters long
// - Maximum 32 characters
// - At least one uppercase letter
// - At least one lowercase letter
// - At least one number
// - At least one special character from !@#$%^&*()_+-=[]{}|;:,.<>?
// Example valid password: "Gx9#vLp!2zQ@dK4&fY"

const passwordPattern =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{}|;:,.<>?])[A-Za-z\d!@#$%^&*()_+\-=\[\]{}|;:,.<>?]{8,32}$/;

// Schema for new user (includes password)
export const newBackendUserValidationSchema = yup.object().shape({
  ...commonValidation,
  password: yup
    .string()
    .required("Password is required")
    .matches(
      passwordPattern,
      "Password must be 8-32 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)"
    ),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords must match")
    .required("Confirm password is required"),
});

// Schema for managing existing user (excludes password)
export const manageBackendUserValidationSchema = yup.object().shape({
  ...commonValidation,
});

// Fields for new user (includes password)
export const newBackendUserFields = [
  ...commonFields.slice(0, 4), // Email through Last Name
  {
    label: "Password",
    name: "password",
    type: "password",
    placeholder: "Enter Password",
    pattern: passwordPattern.source,
    title:
      "Password must be 8-32 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character",
  },
  {
    label: "Confirm Password",
    name: "confirmPassword",
    type: "password",
    placeholder: "Enter Confirm Password",
  },
  ...commonFields.slice(4), // Active through Contact Preference
];

// Fields for managing existing user (excludes password)
export const manageBackendUserFields = [...commonFields];
